import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { Trash, MagnifyingGlass, Prohibit } from "@phosphor-icons/react";
import { AdminAccessNotice } from "@/components/AdminAccessNotice";
import { LegalHoldExport } from "@/components/LegalHoldExport";
import { toast } from "sonner";
import { SourcePermissionsPanel } from "@/components/SourcePermissionsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTakedownPage() {
  const { user, token } = useAuth();
  const [input, setInput] = useState("");
  const [preview, setPreview] = useState(null);
  const [looking, setLooking] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [takedowns, setTakedowns] = useState([]);
  const [blockType, setBlockType] = useState("outlet");
  const [blockValue, setBlockValue] = useState("");
  const [purge, setPurge] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const loadBlocklist = useCallback(() => {
    if (!token) return;
    axios.get(`${API}/admin/source-blocklist`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { setEntries(res.data.entries); setTakedowns(res.data.takedowns); })
      .catch(() => {});
  }, [token]);

  useEffect(() => { loadBlocklist(); }, [loadBlocklist]);

  const extractId = (raw) => {
    const clean = raw.trim().split("?")[0].replace(/\/$/, "");
    return clean.includes("/") ? clean.split("/").pop() : clean;
  };

  const lookup = async (e) => {
    e.preventDefault();
    const id = extractId(input);
    if (!id) return;
    setLooking(true);
    setPreview(null);
    try {
      const [art, stats] = await Promise.all([
        axios.get(`${API}/articles/${id}`).catch(() => null),
        axios.get(`${API}/admin/article-stats/${id}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
      ]);
      if (!art && !stats?.data?.article) {
        toast.error("Article not found — check the URL");
      } else {
        setPreview({ ...(art?.data || stats.data.article), id, removed: !art, stats: stats?.data || null });
      }
    } finally { setLooking(false); }
  };

  const takedown = async () => {
    setRemoving(true);
    try {
      const res = await axios.post(`${API}/admin/takedown-article?article_id=${preview.id}`, {}, { headers });
      if (res.data.removed) {
        toast.success(`Removed & blocked: ${res.data.title?.slice(0, 60)}`);
        setPreview(null);
        setInput("");
        loadBlocklist();
      } else {
        toast.error(res.data.reason || "Not removed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Takedown failed");
    } finally { setRemoving(false); }
  };

  const addBlock = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/source-blocklist`, { type: blockType, value: blockValue, purge }, { headers });
      toast.success(`Blocked ${res.data.type}: ${res.data.value}${res.data.purged_existing ? ` — removed ${res.data.purged_existing} existing articles` : ""}`);
      setBlockValue("");
      loadBlocklist();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add block");
    }
  };

  const removeBlock = async (id) => {
    try {
      await axios.delete(`${API}/admin/source-blocklist/${id}`, { headers });
      toast.success("Block removed");
      loadBlocklist();
    } catch { toast.error("Failed to remove"); }
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        <Header />
        <AdminAccessNotice user={user} area="the takedown tools" />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <span className="px-2 py-1 bg-[#DC2626] text-white text-xs font-mono uppercase tracking-wider">Admin</span>
          <h1 className="font-serif text-3xl text-[#111827] mt-3 mb-1">Article Takedown & Blocklist</h1>
          <p className="text-[#6B6661] text-sm mb-3">Remove an article permanently (it can never be re-scraped), or block whole outlets and authors. Every removal keeps a full copy and its traffic stats in legal hold.</p>
          <LegalHoldExport token={token} />
        </div>

        <div className="bg-white border border-[#E0D4C8] p-5 mb-6">
          <h2 className="font-serif text-lg text-[#111827] mb-3">Remove an article by URL</h2>
          <form onSubmit={lookup} className="flex gap-2 flex-wrap">
            <input value={input} onChange={(e) => setInput(e.target.value)} required data-testid="takedown-url-input"
              placeholder="Paste article URL or ID, e.g. https://www.farmwatch.com/article/abc123..."
              className="flex-1 min-w-[260px] px-4 py-2.5 border border-[#E0D4C8] text-sm focus:outline-none focus:border-[#0F766E]" />
            <button type="submit" disabled={looking} data-testid="takedown-lookup-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#022C22] text-white text-sm font-semibold hover:bg-[#0F766E] transition-colors disabled:opacity-60">
              <MagnifyingGlass size={15} /> {looking ? "Looking up..." : "Look up"}
            </button>
          </form>

          {preview && (
            <div className="mt-4 border-l-4 border-[#DC2626] bg-[#FEF2F2] p-4" data-testid="takedown-preview">
              <p className="font-semibold text-[#33302E]">{preview.title}</p>
              <p className="text-xs text-[#6B6661] mt-1">Source: {preview.source} · {preview.region || (preview.removed ? "removed — copy held in legal hold" : "")} · id {preview.id}</p>
              {preview.summary && <p className="text-sm text-[#4B5563] mt-2 line-clamp-2">{preview.summary}</p>}
              {preview.stats && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid="article-stats">
                  {[
                    ["Page views", preview.stats.page_views],
                    ["Unique visitors", preview.stats.unique_visitors],
                    ["API fetches", preview.stats.api_fetches],
                    ["Seen", preview.stats.first_seen ? `${preview.stats.first_seen} → ${preview.stats.last_seen}` : "—"],
                  ].map(([label, value]) => (
                    <div key={label} className="bg-white border border-[#E0D4C8] px-2 py-2">
                      <div className="text-[10px] uppercase tracking-wider text-[#6B6661] font-mono">{label}</div>
                      <div className="text-sm font-semibold text-[#111827]" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value ?? 0}</div>
                    </div>
                  ))}
                </div>
              )}
              {!preview.removed && (
              <button onClick={takedown} disabled={removing} data-testid="takedown-confirm-btn"
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-[#DC2626] text-white text-sm font-semibold hover:bg-[#B91C1C] transition-colors disabled:opacity-60">
                <Trash size={15} /> {removing ? "Removing..." : "Remove & block permanently (copy + stats kept)"}
              </button>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#E0D4C8] p-5 mb-6">
          <h2 className="font-serif text-lg text-[#111827] mb-3">Block an outlet or author</h2>
          <form onSubmit={addBlock} className="flex gap-2 flex-wrap items-center">
            <select value={blockType} onChange={(e) => setBlockType(e.target.value)} data-testid="block-type-select"
              className="px-3 py-2.5 border border-[#E0D4C8] text-sm bg-white">
              <option value="outlet">Outlet</option>
              <option value="author">Author</option>
            </select>
            <input value={blockValue} onChange={(e) => setBlockValue(e.target.value)} required minLength={2} data-testid="block-value-input"
              placeholder={blockType === "outlet" ? "e.g. Future Farming" : "e.g. Chris McCullough"}
              className="flex-1 min-w-[200px] px-4 py-2.5 border border-[#E0D4C8] text-sm focus:outline-none focus:border-[#0F766E]" />
            <label className="flex items-center gap-1.5 text-sm text-[#6B6661]">
              <input type="checkbox" checked={purge} onChange={(e) => setPurge(e.target.checked)} data-testid="block-purge-checkbox" />
              also remove existing articles
            </label>
            <button type="submit" data-testid="block-add-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#B45309] text-white text-sm font-semibold hover:bg-[#92400E] transition-colors">
              <Prohibit size={15} /> Block
            </button>
          </form>

          {entries.length > 0 && (
            <div className="mt-4 divide-y divide-[#E5E7EB]" data-testid="block-entries-list">
              {entries.map((en) => (
                <div key={en.id} className="py-2 flex items-center justify-between" data-testid={`block-entry-${en.id}`}>
                  <span className="text-sm text-[#33302E]">
                    <span className="px-1.5 py-0.5 bg-[#F6E9DD] text-[#6B6661] text-[10px] font-mono uppercase mr-2">{en.type}</span>
                    {en.value}
                  </span>
                  <button onClick={() => removeBlock(en.id)} data-testid={`block-remove-${en.id}`} className="p-1.5 hover:bg-[#FEF2F2]" title="Unblock">
                    <Trash size={14} className="text-[#DC2626]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <SourcePermissionsPanel token={token} />

        {takedowns.length > 0 && (
          <div className="bg-white border border-[#E0D4C8] p-5">
            <h2 className="font-serif text-lg text-[#111827] mb-3">Taken-down articles ({takedowns.length})</h2>
            <div className="divide-y divide-[#E5E7EB]">
              {takedowns.map((t) => (
                <div key={t.article_id} className="py-2">
                  <p className="text-sm text-[#33302E]">{t.title}</p>
                  <p className="text-[11px] text-[#6B6661]">
                    {t.source} · removed {t.removed_at?.slice(0, 10)}
                    {t.stats && <> · {t.stats.page_views ?? 0} page views · {t.stats.unique_visitors ?? 0} visitors · {t.stats.api_fetches ?? 0} API fetches</>}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
